import { useEffect, useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselFooter,
  CarouselIndicator,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Form,
} from '@qodo/design-system';
import { Download } from 'lucide-react';
import { useLoginGitlabMutation } from 'network/mutations/useLoginGitlabMutation';
import { Step } from 'shared/InstallationDialog';
import { spawn } from 'utils/translations';
import { useMultiStepDialog } from '../../../shared/MultiStepDialog';
import { Checkbox } from '../../../ui/checkbox';
import { downloadFile } from '../../../utils/downloadFile';
import { Image } from '../../Image';
import { getCreateServiceAccountGuideCarouselSteps } from '../configuration';
import { FORM_IDS, QODO_LOGO_FILENAME, QODO_LOGO_URL } from '../consts';
import { useGitlabInstallation } from '../gitlabInstallationContext';
import { CarouselStepConfig } from '../types';

export const CreateServiceAccountGuide = () => {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const { setIsNextEnabled, goNext, setIsPending } = useMultiStepDialog();
  const { isGitlabAppConfigured: isConfigured } = useGitlabInstallation();
  const methods = useForm();

  const { mutate: loginGitlab, isPending: isLoginGitlabPending } =
    useLoginGitlabMutation();

  const spawnCreateServiceAccountGuide = spawn(
    'dialogs.gitlab-installation.create-service-account-guide',
  );
  const carouselSteps = getCreateServiceAccountGuideCarouselSteps();

  useEffect(() => {
    setIsPending(isLoginGitlabPending);
  }, [isLoginGitlabPending]);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    setCurrentSlideIndex(carouselApi.selectedScrollSnap());

    const onSelect = () => {
      setCurrentSlideIndex(carouselApi.selectedScrollSnap());
    };

    carouselApi.on('select', onSelect);

    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi]);

  useLayoutEffect(() => {
    setIsNextEnabled(isConfirmed);
  }, [isConfirmed, setIsNextEnabled]);

  const renderCarouselItem = (step: CarouselStepConfig) => {
    const title = spawnCreateServiceAccountGuide(`${step.id}.title`);

    return (
      <CarouselItem key={step.id} className="basis-full h-full flex flex-col">
        <Image
          source={step.image}
          alt={title}
          title={title}
          className="min-h-0 flex-1 rounded-md opacity-80 object-contain"
        />
      </CarouselItem>
    );
  };

  const renderCarouselIndicator = (step: CarouselStepConfig, index: number) => (
    <CarouselIndicator
      key={step.id}
      index={index}
      isCurrent={index === currentSlideIndex}
      className="uppercase p-0"
    >
      {index + 1}. {spawnCreateServiceAccountGuide(`${step.id}.title`)}
    </CarouselIndicator>
  );

  const renderCarousel = () => (
    <Carousel
      setApi={setCarouselApi}
      className="w-full flex-1 min-h-0 flex flex-col"
    >
      <CarouselContent className="h-full">
        {carouselSteps.map(renderCarouselItem)}
      </CarouselContent>
      <CarouselFooter>
        <CarouselPrevious type="button" />
        <div className="flex items-center justify-center gap-6 font-mono text-sm uppercase">
          {carouselSteps.map(renderCarouselIndicator)}
        </div>
        <CarouselNext type="button" />
      </CarouselFooter>
    </Carousel>
  );

  const onDownloadLogo = () => {
    downloadFile(QODO_LOGO_URL, QODO_LOGO_FILENAME);
  };

  const onSubmit = () => {
    if (isConfigured) {
      loginGitlab(undefined, {
        onSuccess: (response) => {
          window.location.href = response.authorizationUrl;
        },
      });
    } else {
      goNext();
    }
  };

  return (
    <Step
      className="h-full"
      title={spawnCreateServiceAccountGuide('title')}
      description={spawnCreateServiceAccountGuide('description')}
    >
      <Button
        size="xs"
        variant="outline"
        className="group flex gap-1.5 self-start"
        onClick={onDownloadLogo}
      >
        {spawnCreateServiceAccountGuide('download-logo-button')}
        <Download className="stroke-text-action-neutral-idle" size="16px" />
      </Button>
      <div className="flex flex-col flex-1 min-h-0 pt-4 pr-5 pb-2 pl-5 bg-fill-interactive-neutral-idle">
        {renderCarousel()}
      </div>
      <Form
        methods={methods}
        onSubmit={onSubmit}
        {...({ id: FORM_IDS.CREATE_SERVICE_ACCOUNT } as Record<string, string>)}
      >
        <label className="flex items-center gap-2 cursor-pointer ml-auto">
          <Checkbox
            checked={isConfirmed}
            onCheckedChange={(checked) => setIsConfirmed(Boolean(checked))}
          />
          <span className="text-sm text-text-action-neutral-idle font-medium">
            {spawnCreateServiceAccountGuide('confirm-account-created')}
          </span>
        </label>
      </Form>
    </Step>
  );
};
